import { BestPriceCupIcon, NormArticleIcon } from '@synergy/theme';
import { Box, Button } from '@mui/material';
import { FormattedDate, FormattedMessage, FormattedNumber, useIntl } from 'react-intl';
import { Localized } from '@synergy/components';
import {
    PaloAmount,
    PaloColumn,
    PaloCopyText,
    PaloFilterChips,
    PaloListView,
    PaloQuickFilter,
    PaloRowAction,
    paloIsPlaceholderPrice,
} from '@synergy/components-palo';
import { tokens } from '@synergy/theme-palo';
import { ReactElement, ReactNode, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useSearchParams } from 'react-router-dom';

import * as CategoryActions from '../../../../common/actions/category-actions';
import * as ProductActions from '../store/product-actions';
import * as ProductListActions from '../product-list/store/product-actions';
import { PaloFilterPanel } from '../../../../common/components/palo/palo-filter-panel';
import { ProductListItem } from '../product-list/models/product-list-item';
import { ROUTES } from '../../../sidebar-menu/navigation-drawer/routes';
import { useAppSelector } from '../../../../store/use-appstate-selector';
import { PaloSupplierFilter } from '../../../../common/components/palo/palo-account-filter';
import { usePaloColumnFilters } from '../../../../common/components/palo/use-palo-column-filters';
import { usePaloListViews } from '../../../../common/components/palo/use-palo-list-views';
import { usePaloSort } from '../../../../common/components/palo/use-palo-sort';
import { useThunkDispatch } from '../../../../store/use-thunk-dispatch';

export const PaloProductList = (): ReactElement => {
    const { hiddenColumnKeys, shownColumnKeys, toggleColumn, moveColumn, orderColumns, savedFiltersMenu } = usePaloListViews('products');
    const columnFilter = usePaloColumnFilters('products');
    const intl = useIntl();
    const dispatch = useThunkDispatch();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const supplierId = searchParams.get('supplierId') ?? '';
    // What the chip over the list calls the supplier; the filter reports it
    // as soon as it knows (#2239 O-1).
    const [supplierName, setSupplierName] = useState('');
    const { sort, params } = usePaloSort('updatedAt', 'desc');
    const { paginatedData, inProgress } = useAppSelector(state => state.product.list);
    const { currency } = useAppSelector(state => state.settings);

    // The product groups: thirty entries, loaded once and kept.
    const categories = useAppSelector(state => state.categories.items);
    const page = Number(searchParams.get('page') ?? 1); // 1-based
    const limit = Number(searchParams.get('limit') ?? 50);

    useEffect(() => {
        dispatch(ProductListActions.getProductItems(params));
    }, [dispatch, params]);

    useEffect(() => {
        if (0 === categories.length) {
            dispatch(CategoryActions.fetchCategories());
        }
        // Once per mount; the list is what the effect would otherwise chase.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch]);

    // The list keeps its own query, so each filter is handed its value and
    // its writer — the same way the master data lists do it.
    const paramFilter = (key: string, options?: { value: string; label: ReactNode }[]): PaloQuickFilter => ({
        key,
        options,
        value: searchParams.get(key) ?? '',
        onChange: value => {
            const next = new URLSearchParams(searchParams);

            if (value) {
                next.set(key, value);
            } else {
                next.delete(key);
            }

            next.set('page', '1');
            setSearchParams(next);
        },
    });

    const jaNein = [
        { value: 'true', label: <FormattedMessage id="app.common.yes" /> },
        { value: 'false', label: <FormattedMessage id="app.common.no" /> },
    ];

    const updateParams = (changes: Record<string, string>): void => {
        const next = new URLSearchParams(searchParams);
        Object.entries(changes).forEach(([key, value]) => next.set(key, value));
        setSearchParams(next);
    };

    const openDetail = (product: ProductListItem): void =>
        navigate(ROUTES.products.product.detail.replace(':id', product.id.toString()));

    const handleDuplicate = async (id: number): Promise<void> => {
        const duplicated = await dispatch(ProductActions.duplicateProduct(id));
        if (duplicated) {
            navigate(ROUTES.products.product.detail.replace(':id', duplicated.id.toString()));
        }
    };

    const columns: PaloColumn<ProductListItem>[] = [
        {
            key: 'number',
            type: 'articleNumber',
            head: <FormattedMessage id="app.table.column.number" />,
            cell: product => <PaloCopyText value={product.number} />,
        },
        {
            key: 'name',
            type: 'name',
            head: <FormattedMessage id="app.table.column.articleName" />,
            cell: product => <Localized value={product.name} />,
        },
        {
            key: 'gtin',
            type: 'documentNumber',
            head: <FormattedMessage id="app.table.column.gtin" />,
            cell: product => product.globalTradeItemNumber,
        },
        {
            key: 'supplier',
            type: 'name',
            head: <FormattedMessage id="app.table.column.supplier" />,
            cell: product => product.supplier?.name,
        },
        {
            key: 'mainPrice',
            type: 'price',
            sortKey: 'mainPrice',
            currency,
            head: <FormattedMessage id="app.table.column.actualPrice" />,
            cell: product => {
                const price = product.mainPrice[currency];

                if (!price) {
                    return null;
                }

                // A marker, not an amount — printed as money it reads as one
                // (LST-S12).
                return paloIsPlaceholderPrice(price) ? (
                    <Box component="span" sx={{ color: tokens.color.ink3 }}>
                        <FormattedMessage id="app.price.onRequest" />
                    </Box>
                ) : (
                    // Up to four decimals: 12 % of the catalogue carries three
                    // or four, and rounding them to two changes the price
                    // (LST-S1, measured 06.10.2026).
                    <PaloAmount value={price} maximumFractionDigits={4} />
                );
            },
        },
        {
            key: 'bestPrice',
            type: 'price',
            head: <FormattedMessage id="app.priceMatrix.bestPrice" />,
            quickFilter: paramFilter('isBestPrice', jaNein),
            align: 'right',
            cell: product => (product.isBestPrice ? <BestPriceCupIcon style={{ fontSize: 18 }} /> : null),
        },
        {
            key: 'normProduct',
            head: <FormattedMessage id="app.table.column.norm" />,
            quickFilter: paramFilter('isNormArticle', jaNein),
            align: 'right',
            cell: product => (product.isNormArticle ? <NormArticleIcon style={{ fontSize: 18 }} /> : null),
        },
        {
            key: 'updatedAt',
            type: 'date',
            sortKey: 'updatedAt',
            head: <FormattedMessage id="app.table.column.updatedAt" />,
            cell: product => <FormattedDate value={product.updatedAt} year="numeric" month="short" day="2-digit" />,
        },
    ];

    const rowActions = (product: ProductListItem): PaloRowAction[] => [
        { label: 'Öffnen', onClick: () => openDetail(product) },
        { label: 'Duplizieren', onClick: () => handleDuplicate(product.id) },
    ];

    const headerActions = (
        <Button variant="contained" color="primary" onClick={() => navigate(ROUTES.products.product.new)}>
            <FormattedMessage id="app.product.detail.new" />
        </Button>
    );

    return (
        <PaloListView<ProductListItem>
            filterChips={
                <PaloFilterChips
                    names={supplierName ? { supplierId: [{ value: supplierId, label: supplierName }] } : undefined}
                />
            }
            quickFilters={[
                {
                    key: 'supplierId',
                    label: <FormattedMessage id="app.table.column.supplier" />,
                    control: (
                        <PaloSupplierFilter
                            value={supplierId}
                            onName={setSupplierName}
                            onChange={value => {
                                const next = new URLSearchParams(searchParams);

                                if (value) {
                                    next.set('supplierId', value);
                                } else {
                                    next.delete('supplierId');
                                }

                                next.set('page', '1');
                                setSearchParams(next);
                            }}
                        />
                    ),
                },
                {
                    label: <FormattedMessage id="app.table.column.productGroup" />,
                    // The endpoint reads this one as an array — a scalar
                    // `categoryIds=77` is accepted and then ignored.
                    filter: paramFilter(
                        'categoryIds[]',
                        categories.map(category => ({
                            value: String(category.id),
                            label: <Localized value={category.translations} />,
                        }))
                    ),
                },
            ]}
            title={<FormattedMessage id="app.navigation.product" />}
            pageTitle={intl.formatMessage({ id: 'app.navigation.product' })}
            sort={sort}
            onSortChange={(key, order) => updateParams({ sort: key, order, page: '1' })}
            searchValue={searchParams.get('search') ?? ''}
            onSearchChange={value => {
                const next = new URLSearchParams(searchParams);
                if (value) {
                    next.set('search', value);
                } else {
                    next.delete('search');
                }
                next.set('page', '1');
                setSearchParams(next, { replace: true });
            }}
            headerActions={headerActions}
            filters={
                <>
                    <PaloFilterPanel listKey="products" />
                    {savedFiltersMenu}
                </>
            }
            columns={orderColumns(columns)}
            columnFilter={columnFilter}
            onMoveColumn={moveColumn}
            hiddenColumnKeys={hiddenColumnKeys}
            shownColumnKeys={shownColumnKeys}
            onToggleColumn={toggleColumn}
            items={paginatedData.items}
            getRowId={product => product.id}
            requesting={inProgress}
            emptyLabel="Keine Produkte."
            onRowClick={openDetail}
            rowActions={rowActions}
            pagination={{
                count: paginatedData.total,
                page,
                limit,
                onPageChange: newPage => updateParams({ page: String(newPage) }),
                onLimitChange: newLimit => updateParams({ limit: String(newLimit), page: '1' }),
            }}
        />
    );
};
